/**
 * useGameStats Hook
 * Fetch user game statistics and leaderboards
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { UserGameStats, LeaderboardEntry } from "../types";

interface UseGameStatsReturn {
  /** Current user's stats for all games */
  myStats: UserGameStats[];
  /** Current user's stats for a specific game */
  getMyStats: (gameSlug: string) => UserGameStats | undefined;
  /** Global leaderboard (all games combined) */
  globalLeaderboard: LeaderboardEntry[];
  /** Game-specific leaderboard */
  gameLeaderboard: LeaderboardEntry[];
  /** Current user's rank in global leaderboard */
  myGlobalRank: number | null;
  /** Current user's rank in game leaderboard */
  myGameRank: number | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Fetch leaderboard for a specific game */
  fetchGameLeaderboard: (gameSlug: string, limit?: number) => Promise<void>;
  /** Refetch all stats */
  refetch: () => Promise<void>;
}

interface UseGameStatsOptions {
  /** Game slug to fetch specific leaderboard */
  gameSlug?: string;
  /** Number of entries in leaderboard */
  leaderboardLimit?: number;
  /** Auto-fetch on mount */
  autoFetch?: boolean;
}

/**
 * Hook to access game statistics and leaderboards
 */
export const useGameStats = (
  options: UseGameStatsOptions = {}
): UseGameStatsReturn => {
  const { gameSlug, leaderboardLimit = 50, autoFetch = true } = options;
  const { activeProfile } = useAuth();

  const [myStats, setMyStats] = useState<UserGameStats[]>([]);
  const [globalLeaderboard, setGlobalLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [gameLeaderboard, setGameLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch current user's stats for all games
   */
  const fetchMyStats = useCallback(async () => {
    if (!activeProfile?.id) return [];

    const { data, error: fetchError } = await supabase
      .from("user_game_stats")
      .select(
        `
        *,
        game:game_definitions(*)
      `
      )
      .eq("user_id", activeProfile.id)
      .order("total_points_earned", { ascending: false });

    if (fetchError) {
      throw new Error(fetchError.message);
    }

    return data as UserGameStats[];
  }, [activeProfile?.id]);

  /**
   * Fetch global leaderboard (all games)
   */
  const fetchGlobalLeaderboard = useCallback(
    async (limit: number = leaderboardLimit) => {
      const { data, error: fetchError } = await supabase.rpc(
        "get_game_leaderboard",
        {
          p_game_slug: null,
          p_limit: limit,
        }
      );

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      return data as LeaderboardEntry[];
    },
    [leaderboardLimit]
  );

  /**
   * Fetch game-specific leaderboard
   */
  const fetchGameLeaderboard = useCallback(
    async (slug: string, limit: number = leaderboardLimit) => {
      setIsLoading(true);

      try {
        const { data, error: fetchError } = await supabase.rpc(
          "get_game_leaderboard",
          {
            p_game_slug: slug,
            p_limit: limit,
          }
        );

        if (fetchError) {
          throw new Error(fetchError.message);
        }

        setGameLeaderboard(data as LeaderboardEntry[]);
      } catch (err) {
        console.error("Error fetching game leaderboard:", err);
        setError(err instanceof Error ? err : new Error("Failed to fetch leaderboard"));
      } finally {
        setIsLoading(false);
      }
    },
    [leaderboardLimit]
  );

  /**
   * Fetch all data
   */
  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [statsData, globalData] = await Promise.all([
        fetchMyStats(),
        fetchGlobalLeaderboard(),
      ]);

      setMyStats(statsData);
      setGlobalLeaderboard(globalData);

      // Fetch game-specific leaderboard if slug provided
      if (gameSlug) {
        const { data: gameData, error: gameError } = await supabase.rpc(
          "get_game_leaderboard",
          {
            p_game_slug: gameSlug,
            p_limit: leaderboardLimit,
          }
        );

        if (!gameError && gameData) {
          setGameLeaderboard(gameData as LeaderboardEntry[]);
        }
      }
    } catch (err) {
      console.error("Error fetching game stats:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch stats"));
    } finally {
      setIsLoading(false);
    }
  }, [fetchMyStats, fetchGlobalLeaderboard, gameSlug, leaderboardLimit]);

  // Initial fetch
  useEffect(() => {
    if (autoFetch) {
      refetch();
    }
  }, [autoFetch, refetch]);

  /**
   * Get stats for a specific game
   */
  const getMyStats = useCallback(
    (slug: string): UserGameStats | undefined => {
      return myStats.find((s) => s.game_slug === slug);
    },
    [myStats]
  );

  /**
   * Calculate user's global rank
   */
  const myGlobalRank = useMemo((): number | null => {
    if (!activeProfile?.id || globalLeaderboard.length === 0) return null;

    const entry = globalLeaderboard.find((e) => e.user_id === activeProfile.id);
    return entry?.rank ?? null;
  }, [activeProfile?.id, globalLeaderboard]);

  /**
   * Calculate user's game-specific rank
   */
  const myGameRank = useMemo((): number | null => {
    if (!activeProfile?.id || gameLeaderboard.length === 0) return null;

    const entry = gameLeaderboard.find((e) => e.user_id === activeProfile.id);
    return entry?.rank ?? null;
  }, [activeProfile?.id, gameLeaderboard]);

  return {
    myStats,
    getMyStats,
    globalLeaderboard,
    gameLeaderboard,
    myGlobalRank,
    myGameRank,
    isLoading,
    error,
    fetchGameLeaderboard,
    refetch,
  };
};

/**
 * Utility hook to get stats for a single game
 */
export const useSingleGameStats = (gameSlug: string) => {
  const { getMyStats, gameLeaderboard, myGameRank, isLoading, error, refetch } =
    useGameStats({ gameSlug, autoFetch: true });

  const stats = useMemo(() => getMyStats(gameSlug), [getMyStats, gameSlug]);

  return {
    stats,
    leaderboard: gameLeaderboard,
    myRank: myGameRank,
    isLoading,
    error,
    refetch,
  };
};

